import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import SubNav from '../../components/SubNav/SubNav';
import { useAuth } from '../../contexts/AuthContext';
import { isOwnerToolsUser } from '../../utils/ownerTools';
import { llmService } from '../../services/llmService';
import { useVisualViewportLock } from '../../hooks/useVisualViewportLock';
import '../../styles/Dashboard.css';
import './LlmChat.css';

const LlmChat = () => {
  const { user, loading } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const listRef = useRef(null);
  const composerRef = useRef(null);
  const streamRef = useRef('');
  const [historyOpen, setHistoryOpen] = useState(false);

  useVisualViewportLock();

  const loadConversations = useCallback(async () => {
    const rows = await llmService.listConversations();
    setConversations(rows);
    return rows;
  }, []);

  useEffect(() => {
    if (!user || !isOwnerToolsUser(user)) return undefined;
    loadConversations().catch((err) => setError(err.message));
    return undefined;
  }, [user, loadConversations]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, status]);

  if (loading) {
    return (
      <div className="dashboard-page">
        <Header />
        <SubNav />
        <div className="dashboard-container">Loading...</div>
      </div>
    );
  }

  if (!isOwnerToolsUser(user)) {
    return <Navigate to="/" replace />;
  }

  const openConversation = async (id) => {
    setError('');
    setActiveId(id);
    const detail = await llmService.getConversation(id);
    setMessages(detail.messages || []);
  };

  const handleNewChat = async () => {
    setError('');
    try {
      const created = await llmService.createConversation();
      setConversations((current) => [created, ...current.filter((row) => row.id !== created.id)]);
      setActiveId(created.id);
      setMessages([]);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteConvo = async () => {
    if (!activeId) return;
    if (!window.confirm('Delete this conversation?')) return;
    await llmService.deleteConversation(activeId);
    setActiveId(null);
    setMessages([]);
    await loadConversations();
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Clear all LLM history?')) return;
    await llmService.clearHistory();
    setActiveId(null);
    setMessages([]);
    setConversations([]);
  };

  const copyText = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    } catch {
      setError('Could not copy');
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const message = draft.trim();
    if (!message || sending) return;

    setSending(true);
    setError('');
    setDraft('');
    setStatus('searching');
    streamRef.current = '';
    setMessages((current) => [
      ...current,
      { role: 'user', content: message },
      { role: 'assistant', content: '' },
    ]);

    await llmService.chat({
      conversationId: activeId,
      message,
      onConversation: (conversation) => {
        setActiveId(conversation.id);
        setConversations((current) => {
          const rest = current.filter((row) => row.id !== conversation.id);
          return [{ id: conversation.id, title: conversation.title }, ...rest];
        });
      },
      onStatus: (nextStatus) => setStatus(nextStatus),
      onToken: (token) => {
        streamRef.current += token;
        const snapshot = streamRef.current;
        setMessages((current) => {
          const next = [...current];
          next[next.length - 1] = { role: 'assistant', content: snapshot };
          return next;
        });
      },
      onDone: () => {
        setStatus('');
        setSending(false);
        loadConversations().catch(() => {});
      },
      onError: (err) => {
        setError(err);
        setStatus('');
        setSending(false);
      },
    });
  };

  return (
    <div className="dashboard-page llm-page owner-tool-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>LLM | NowTarot AI</title>
      </Helmet>
      <Header />
      <SubNav />
      <div className="llm-shell">
        <aside className={`llm-sidebar${historyOpen ? ' open' : ''}`}>
          <div className="llm-sidebar-head">
            <button
              type="button"
              className="llm-history-toggle"
              onClick={() => setHistoryOpen((open) => !open)}
              aria-expanded={historyOpen}
            >
              {historyOpen ? 'Hide chats' : 'Chats'}
            </button>
            <button type="button" className="btn btn-primary llm-new-btn" onClick={handleNewChat}>
              New chat
            </button>
          </div>
          <div className="llm-convo-list">
            {conversations.map((conversation) => (
              <button
                key={conversation.id}
                type="button"
                className={`llm-convo-item${conversation.id === activeId ? ' active' : ''}`}
                onClick={() => {
                  openConversation(conversation.id);
                  setHistoryOpen(false);
                }}
              >
                {conversation.title}
              </button>
            ))}
            {conversations.length === 0 && (
              <p className="llm-empty">No conversations yet.</p>
            )}
          </div>
          <div className="llm-sidebar-actions">
            <button type="button" className="llm-text-btn" onClick={handleDeleteConvo} disabled={!activeId}>
              Delete convo
            </button>
            <button type="button" className="llm-text-btn danger" onClick={handleClearHistory}>
              Clear history
            </button>
          </div>
        </aside>

        <section className="llm-main">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="llm-messages" ref={listRef}>
            {messages.length === 0 && (
              <div className="llm-placeholder">
                <h2>Ask anything</h2>
                <p>This chat can search the web for current prices, products, and facts.</p>
              </div>
            )}
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`llm-bubble ${message.role}`}>
                <div className="llm-bubble-top">
                  <span>{message.role === 'user' ? 'You' : 'LLM'}</span>
                  {message.content && (
                    <button type="button" onClick={() => copyText(message.content, index)}>
                      {copiedIndex === index ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </div>
                <div className="llm-bubble-body">{message.content || (sending && index === messages.length - 1 ? '...' : '')}</div>
              </div>
            ))}
            {status === 'searching' && <div className="llm-status">Searching the web…</div>}
          </div>
          <form className="llm-composer" onSubmit={handleSend}>
            <textarea
              ref={composerRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask anything, or paste a long conversation to analyze…"
              rows={2}
              enterKeyHint="send"
              onFocus={() => {
                window.setTimeout(() => {
                  composerRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                  if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                  }
                }, 50);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
            />
            <button type="submit" className="btn btn-primary" disabled={sending || !draft.trim()}>
              {sending ? 'Thinking…' : 'Send'}
            </button>
          </form>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default LlmChat;
